import { Banner } from '../../components/Banner';
import { Button } from '../../components/Button';
import type { ScanWeekData } from './types';

const DAY_SHORT = ['do', 'lu', 'ma', 'mi', 'ju', 'vi', 'sá'];
const DAY_LONG = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];

function dayIndex(date: string): number {
  // Las fechas vienen como día calendario (`2026-09-15`): se leen en local para no correrse un día.
  const d = new Date(`${date}T12:00:00`);
  return Number.isNaN(d.getTime()) ? -1 : d.getDay();
}

export interface ScanWeekProps {
  data: ScanWeekData | null;
  loading?: boolean;
  /** Mensaje del fallo de la carga: sin datos no es lo mismo que no haber podido pedirlos (BUG-25). */
  error?: string | null;
  onRetry?: () => void;
}

/** Escaneos de la semana (design-spec §3): siete números rotulados, sin ejes ni grilla. */
export function ScanWeek({ data, loading = false, error = null, onRetry }: ScanWeekProps) {
  if (error) {
    return (
      <Banner
        tone="error"
        action={
          onRetry ? (
            <Button size="sm" variant="secondary" onClick={onRetry}>
              Reintentar
            </Button>
          ) : undefined
        }
      >
        No pudimos cargar los escaneos. {error}
      </Banner>
    );
  }
  if (loading) {
    return (
      <div role="status" className="h-28 w-full animate-pulse rounded-md bg-gray-100 motion-reduce:animate-none" aria-label="Cargando los escaneos de la semana" />
    );
  }
  const days = data?.days ?? [];
  if (days.length === 0) {
    return <p className="text-sm text-ink-muted">Todavía no hay escaneos de este código.</p>;
  }
  const max = Math.max(...days.map((d) => d.count), 1);
  const description = days
    .map((d) => {
      const i = dayIndex(d.date);
      return `${i >= 0 ? DAY_LONG[i] : d.date} ${d.count}`;
    })
    .join(', ');

  return (
    <div className="space-y-2">
      <div role="img" aria-label={`Escaneos por día: ${description}.`} className="flex h-28 items-end gap-2">
        {days.map((d) => {
          const i = dayIndex(d.date);
          return (
            <div key={d.date} className="flex h-full flex-1 flex-col items-center justify-end gap-1">
              <span className="text-xs font-semibold tabular-nums text-ink">{d.count.toLocaleString('es-CL')}</span>
              <span className="w-full rounded-t-sm bg-green" style={{ height: `${Math.max(2, Math.round((d.count / max) * 100))}%` }} aria-hidden="true" />
              <span className="text-xs text-ink-muted">{i >= 0 ? DAY_SHORT[i] : d.date}</span>
            </div>
          );
        })}
      </div>
      <p className="text-xs tabular-nums text-ink-muted">
        {(data?.total ?? 0).toLocaleString('es-CL')} escaneos en total
        {data?.lastHit ? ` · último: ${new Date(data.lastHit).toLocaleString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}` : ''}
      </p>
      <p className="text-xs text-ink-muted">
        Cada escaneo guarda fecha, país, dispositivo y la IP cifrada; nunca se muestra en claro. Se conserva aunque el código se dé de baja.
      </p>
    </div>
  );
}
